import React from 'react';
import { ArrowLeftIcon, AlertCircleIcon, ClockIcon, CheckCircleIcon } from 'lucide-react';
interface MedicationReviewPageProps {
  onBackToOverview: () => void;
}
export const MedicationReviewPage: React.FC<MedicationReviewPageProps> = ({
  onBackToOverview
}) => {
  // Sample data for residents needing medication review
  const residents = [{
    id: 1,
    name: 'Martha Williams',
    age: 82,
    photo: 'https://images.unsplash.com/photo-1566616213894-2d4e1baee5d8?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=100&q=80',
    room: '205',
    medications: ['Lisinopril', 'Metformin', 'Simvastatin', 'Levothyroxine'],
    lastReview: '2023-06-12',
    priority: 'high',
    notes: 'Blood pressure fluctuations noted in last check'
  }, {
    id: 2,
    name: 'George Taylor',
    age: 78,
    photo: 'https://images.unsplash.com/photo-1557862921-37829c790f19?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=100&q=80',
    room: '118',
    medications: ['Amlodipine', 'Warfarin', 'Furosemide'],
    lastReview: '2023-06-15',
    priority: 'high',
    notes: 'INR levels need monitoring'
  }, {
    id: 3,
    name: 'Susan Martinez',
    age: 85,
    photo: 'https://images.unsplash.com/photo-1498757581981-8ddb3c0b9b07?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=100&q=80',
    room: '327',
    medications: ['Gabapentin', 'Sertraline', 'Omeprazole', 'Tramadol'],
    lastReview: '2023-06-10',
    priority: 'medium',
    notes: 'Recent pain management changes'
  }, {
    id: 4,
    name: 'Richard Johnson',
    age: 76,
    photo: 'https://images.unsplash.com/photo-1556474835-b0f3ac40d4d1?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=100&q=80',
    room: '142',
    medications: ['Metoprolol', 'Atorvastatin', 'Albuterol'],
    lastReview: '2023-06-18',
    priority: 'medium',
    notes: 'Recent COPD exacerbation'
  }, {
    id: 5,
    name: 'Elizabeth Brown',
    age: 81,
    photo: 'https://images.unsplash.com/photo-1566616213894-2d4e1baee5d8?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=100&q=80',
    room: '219',
    medications: ['Donepezil', 'Memantine', 'Citalopram'],
    lastReview: '2023-06-20',
    priority: 'low',
    notes: 'Stable on current regimen'
  }, {
    id: 6,
    name: 'David Wilson',
    age: 79,
    photo: 'https://images.unsplash.com/photo-1566616213894-2d4e1baee5d8?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=100&q=80',
    room: '156',
    medications: ['Losartan', 'Metformin', 'Glipizide'],
    lastReview: '2023-06-22',
    priority: 'low',
    notes: 'Recent A1C within target range'
  }];
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };
  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case 'high':
        return <AlertCircleIcon className="h-5 w-5 text-red-500" data-id="element-186" />;
      case 'medium':
        return <ClockIcon className="h-5 w-5 text-orange-500" data-id="element-187" />;
      case 'low':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" data-id="element-188" />;
      default:
        return null;
    }
  };
  return <main className="flex-1 overflow-y-auto bg-gray-50 p-6" data-id="element-189">
      <div className="mb-6" data-id="element-190">
        <div className="flex items-center justify-between mb-4" data-id="element-191">
          <h1 className="text-2xl font-bold" data-id="element-192">Medication Review Report</h1>
          <button onClick={onBackToOverview} className="flex items-center px-4 py-2 bg-[#1f58b0] text-white rounded-md hover:bg-[#255db7] transition-colors" data-id="element-193">
            <ArrowLeftIcon className="h-4 w-4 mr-2" data-id="element-194" />
            Back to Overview
          </button>
        </div>
        <p className="text-gray-600 mb-4" data-id="element-195">
          This report identifies residents who need medication reviews this
          week, prioritized by urgency and last review date.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6" data-id="element-196">
          <div className="bg-white p-4 rounded-lg shadow" data-id="element-197">
            <h3 className="text-sm font-medium text-gray-500" data-id="element-198">
              Total Reviews Needed
            </h3>
            <p className="text-2xl font-bold text-[#1f58b0]" data-id="element-199">6</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow" data-id="element-200">
            <h3 className="text-sm font-medium text-gray-500" data-id="element-201">
              Average Medications per Resident
            </h3>
            <p className="text-2xl font-bold text-[#f97c6b]" data-id="element-202">3.5</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow" data-id="element-203">
            <h3 className="text-sm font-medium text-gray-500" data-id="element-204">
              High Priority Reviews
            </h3>
            <p className="text-2xl font-bold text-[#f6d052]" data-id="element-205">2</p>
          </div>
        </div>
      </div>
      <div className="bg-white rounded-lg shadow" data-id="element-206">
        <div className="p-4 border-b" data-id="element-207">
          <h2 className="text-lg font-semibold" data-id="element-208">
            Residents Needing Medication Review
          </h2>
          <p className="text-sm text-gray-500" data-id="element-209">
            Sorted by priority and last review date
          </p>
        </div>
        <div className="overflow-x-auto" data-id="element-210">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-211">
            <thead className="bg-gray-50" data-id="element-212">
              <tr data-id="element-213">
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-214">
                  Resident
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-215">
                  Room
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-216">
                  Medications
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-217">
                  Last Review
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-218">
                  Priority
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-219">
                  Notes
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-220">
              {residents.map(resident => <tr key={resident.id} className="hover:bg-gray-50" data-id="element-221">
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-222">
                    <div className="flex items-center" data-id="element-223">
                      <div className="flex-shrink-0 h-10 w-10" data-id="element-224">
                        <img className="h-10 w-10 rounded-full object-cover" src={resident.photo} alt={resident.name} data-id="element-225" />
                      </div>
                      <div className="ml-4" data-id="element-226">
                        <div className="text-sm font-medium text-gray-900" data-id="element-227">
                          {resident.name}
                        </div>
                        <div className="text-sm text-gray-500" data-id="element-228">
                          {resident.age} years
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-229">
                    {resident.room}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-230">
                    <div className="text-sm text-gray-900" data-id="element-231">
                      {resident.medications.length} medications
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-232">
                      {resident.medications.join(', ')}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-233">
                    {formatDate(resident.lastReview)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-234">
                    <div className="flex items-center" data-id="element-235">
                      {getPriorityIcon(resident.priority)}
                      <span className="ml-1 text-sm text-gray-700 capitalize" data-id="element-236">
                        {resident.priority}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500" data-id="element-237">
                    {resident.notes}
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>
    </main>;
};